import { useState, useEffect, useRef } from "react";
import domain from "../../../../reusecomponent/domain";
import { getMedia, saveMedia } from "../mediaCache";
import { WorningToastContainer } from "../../../function/CustomFunction";
import { useSelector } from "react-redux";

// ─── AudioPreview ─────────────────────────────────────────────────────────────
// Used inside the chat message bubble (full width player).
export function AudioPreview({ chat, sending_type }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const Accesstoken = useSelector((state) => state.Token.value);

  const loadAudio = async () => {
    setLoading(true);
    setAudioUrl(null);
    setProgress(0);

    try {
      const fileid = chat.file_id;
      if (!fileid) { setLoading(false); return; }

      // 1) Check cache first
      const localBlob = await getMedia(chat.body_type, chat.type, fileid);
      if (localBlob.exists) {
        if (localBlob.value == null) { setLoading(false); return; }
        setAudioUrl(URL.createObjectURL(localBlob.value));
        setLoading(false);
        return;
      }

      // 2) Fetch from server — FIXED: was incorrectly calling `/image/`, now correctly calls `/audio/`
      const fileResponse = await fetch(
        `${domain()}/whatsapp/${sending_type}/audio/${fileid}`,
        { method: "GET", headers: { Authorization: `Bearer ${Accesstoken}` } }
      );
      if (fileResponse.ok) {
        const blob = await fileResponse.blob();
        await saveMedia(chat.body_type, chat.type, fileid, blob);
        setAudioUrl(URL.createObjectURL(blob));
      } else {
        WorningToastContainer("Audio not found on server!");
        await saveMedia(chat.body_type, chat.type, fileid, null);
      }
    } catch (err) {
      // Network failure — fail silently, loading will reset
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudio();
    const audio = audioRef.current;
    return () => {
      if (audio) { audio.pause(); audio.src = ""; }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.file_id]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || loading) return;
    try {
      if (audio.paused) { await audio.play(); }
      else { audio.pause(); }
    } catch (_) { /* interrupted — safe to ignore */ }
  };

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  return (
    <div
      style={{
        background: "#d3f8c6",
        padding: "12px",
        borderRadius: "10px",
        width: "280px",
        display: "flex",
        alignItems: "center",
        position: "relative",
      }}
    >
      {loading ? (
        <div className="spinner-border spinner-border-sm text-secondary" role="status">
          <span className="visually-hidden">Loading audio…</span>
        </div>
      ) : (
        <>
          <div
            style={{
              width: "45px",
              height: "45px",
              background: "#1c7dfc",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              fontSize: "22px",
              marginRight: "10px",
              flexShrink: 0,
            }}
          >
            🎵
          </div>

          <button
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause audio" : "Play audio"}
            style={{ background: "transparent", border: "none", cursor: "pointer", marginRight: "8px" }}
          >
            {isPlaying ? (
              <svg width="26" height="26" fill="#1c7dfc"><rect x="6" y="5" width="4" height="16" rx="1" /><rect x="16" y="5" width="4" height="16" rx="1" /></svg>
            ) : (
              <svg width="30" height="30" fill="#1c7dfc"><path d="M8 5v14l11-7z" /></svg>
            )}
          </button>

          <div style={{ flex: 1 }}>
            <div style={{ height: "3px", background: "#bbb", borderRadius: "2px", overflow: "hidden", marginBottom: "5px" }}>
              <div style={{ height: "100%", width: `${progress}%`, background: "#1c7dfc", transition: "width 0.1s linear" }} />
            </div>
            <div style={{ fontSize: "12px", color: "#555" }}>{chat.duration || "0:30"}</div>
          </div>

          <audio
            ref={audioRef}
            src={audioUrl}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => { setIsPlaying(false); setProgress(0); }}
            onTimeUpdate={onTimeUpdate}
          />
        </>
      )}
    </div>
  );
}

// ─── AudioPreviews ────────────────────────────────────────────────────────────
// Used inside the MediaTabs panel (compact thumbnail view, cache-only).
export function AudioPreviews({ chat }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  const loadAudio = async () => {
    setLoading(true);
    setAudioUrl(null);
    setProgress(0);

    try {
      const fileid = chat.file_id;
      if (!fileid) { setLoading(false); return; }

      // Cache-only — no server fetch for the media panel
      const localBlob = await getMedia(chat.body_type, chat.type, fileid);
      if (localBlob.exists) {
        if (localBlob.value == null) { setLoading(false); return; }
        setAudioUrl(URL.createObjectURL(localBlob.value));
      }
    } catch (_) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudio();
    const audio = audioRef.current;
    return () => {
      if (audio) { audio.pause(); audio.src = ""; }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.file_id]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || loading) return;
    try {
      if (audio.paused) { await audio.play(); }
      else { audio.pause(); }
    } catch (_) { /* interrupted */ }
  };

  const onTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  return (
    <div
      style={{
        background: "#d3f8c6",
        padding: "10px",
        borderRadius: "10px",
        width: "100px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
      }}
    >
      {loading ? (
        <div className="spinner-border spinner-border-sm text-secondary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      ) : (
        <>
          <button
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
            style={{ background: "transparent", border: "none", cursor: "pointer", marginBottom: "4px" }}
          >
            {isPlaying ? (
              <svg width="26" height="26" fill="#1c7dfc"><rect x="6" y="5" width="4" height="16" rx="1" /><rect x="16" y="5" width="4" height="16" rx="1" /></svg>
            ) : (
              <svg width="30" height="30" fill="#1c7dfc"><path d="M8 5v14l11-7z" /></svg>
            )}
          </button>

          <div style={{ width: "100%", height: "3px", background: "#bbb", borderRadius: "2px", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: "#1c7dfc", transition: "width 0.1s linear" }} />
          </div>
          <div style={{ fontSize: "11px", color: "#555", marginTop: "3px" }}>{chat.duration || "0:30"}</div>

          <audio
            ref={audioRef}
            src={audioUrl}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => { setIsPlaying(false); setProgress(0); }}
            onTimeUpdate={onTimeUpdate}
          />
        </>
      )}
    </div>
  );
}